import React, { FC, useCallback, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../api';
import './daily-briefing-section.scss';

// Mirrors GetEveningSweepSettingsResponse / UpdateEveningSweepSettingsRequest
// from @dreamai/types; the published package predates them.
interface EveningSweepSettings {
  enabled: boolean;
  /** "HH:MM" (24h) in the user's timezone. */
  sweepTime: string;
}
interface GetEveningSweepSettingsResponse {
  success: boolean;
  settings: EveningSweepSettings | null;
}
interface UpdateEveningSweepSettingsRequest {
  enabled?: boolean;
  sweepTime?: string;
}

const DEFAULT_SWEEP_TIME = '19:00';

const EveningSweepSection: FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [savingEnabled, setSavingEnabled] = useState(false);
  const [sweepTime, setSweepTime] = useState(DEFAULT_SWEEP_TIME);
  const [savedSweepTime, setSavedSweepTime] = useState(DEFAULT_SWEEP_TIME);
  const [savingTime, setSavingTime] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getAccessTokenSilently();
        const res = await api.get<GetEveningSweepSettingsResponse>(
          '/evening-sweep/settings',
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (cancelled) return;
        if (res.data.success && res.data.settings) {
          const time = res.data.settings.sweepTime.slice(0, 5);
          setEnabled(res.data.settings.enabled);
          setSweepTime(time);
          setSavedSweepTime(time);
        }
      } catch (err) {
        console.error('Error fetching evening round-up settings:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getAccessTokenSilently]);

  const putSettings = useCallback(
    async (body: UpdateEveningSweepSettingsRequest) => {
      const token = await getAccessTokenSilently();
      await api.put('/evening-sweep/settings', body, {
        headers: { Authorization: `Bearer ${token}` },
      });
    },
    [getAccessTokenSilently],
  );

  const handleToggleEnabled = useCallback(async () => {
    const next = !enabled;
    setEnabled(next);
    setSavingEnabled(true);
    try {
      await putSettings({ enabled: next });
    } catch (err) {
      console.error('Error updating evening round-up on/off:', err);
      setEnabled(!next);
    } finally {
      setSavingEnabled(false);
    }
  }, [enabled, putSettings]);

  const handleTimeBlur = useCallback(async () => {
    if (!sweepTime || sweepTime === savedSweepTime) return;
    setSavingTime(true);
    try {
      await putSettings({ sweepTime });
      setSavedSweepTime(sweepTime);
    } catch (err) {
      console.error('Error updating evening round-up time:', err);
      setSweepTime(savedSweepTime);
    } finally {
      setSavingTime(false);
    }
  }, [sweepTime, savedSweepTime, putSettings]);

  return (
    <div className='daily-briefing-section evening-sweep-section'>
      <div className='daily-briefing-section-header'>
        <h2 className='section-title'>Evening Round-up</h2>
        <p className='daily-briefing-section-subtitle'>
          A short end-of-day message: what slipped, what you said you'd do, and
          what's waiting for you in the morning.
        </p>
      </div>

      <div className='daily-briefing-section-card'>
        {loading ? (
          <p className='daily-briefing-section-loading'>Loading...</p>
        ) : (
          <>
            <div className='daily-briefing-section-row'>
              <div className='daily-briefing-section-row-info'>
                <span className='daily-briefing-section-row-label'>
                  Evening Round-up
                </span>
                <span className='daily-briefing-section-row-status'>
                  {enabled
                    ? 'On — your round-up is sent at the time below when there is something worth raising.'
                    : 'Your evening round-up is off.'}
                </span>
              </div>
              <button
                className={`toggle-switch${enabled ? ' on' : ''}`}
                onClick={handleToggleEnabled}
                disabled={savingEnabled}
                aria-label='Evening Round-up'
                role='switch'
                aria-checked={enabled}
              >
                <span className='toggle-knob' />
              </button>
            </div>

            {enabled && (
              <div className='daily-briefing-section-row'>
                <div className='daily-briefing-section-row-info'>
                  <span className='daily-briefing-section-row-label'>
                    Round-up time
                  </span>
                  <span className='daily-briefing-section-row-status'>
                    Your round-up is prepared from this time each day, in your
                    local timezone.
                  </span>
                </div>
                <input
                  type='time'
                  className='daily-briefing-section-time-input'
                  min='12:00'
                  value={sweepTime}
                  onChange={(e) => setSweepTime(e.target.value)}
                  onBlur={handleTimeBlur}
                  disabled={savingTime}
                  aria-label='Evening round-up time'
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default EveningSweepSection;
